import { normalizeCoordinate } from "./tripMapCamera.js";

export const tripGeolocationWatchOptions = Object.freeze({
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 27000,
});

const initialState = Object.freeze({
  permission: "unknown",
  status: "idle",
  location: null,
  error: null,
});

function normalizeError(error) {
  return {
    code: Number(error?.code) || 0,
    message: String(error?.message || "Unable to get your location."),
  };
}

export function createTripGeolocationSession({
  geolocation,
  onStateChange = () => {},
  watchOptions = tripGeolocationWatchOptions,
} = {}) {
  let state = { ...initialState };
  let session = 0;
  let watchId = null;
  let enabled = false;

  const emit = (patch) => {
    state = { ...state, ...patch };
    onStateChange(state);
  };

  const clearCurrentWatch = () => {
    if (watchId === null || !geolocation?.clearWatch) return;
    const resolvedWatchId = watchId;
    watchId = null;
    geolocation.clearWatch(resolvedWatchId);
  };

  const invalidateSession = () => {
    session += 1;
    clearCurrentWatch();
  };

  const stop = () => {
    enabled = false;
    invalidateSession();
    emit({ status: "idle", location: null, error: null });
  };

  const start = () => {
    if (enabled && (state.status === "requesting" || state.status === "active")) {
      return;
    }

    enabled = true;
    invalidateSession();

    if (!geolocation?.watchPosition) {
      enabled = false;
      emit({
        permission: "unsupported",
        status: "error",
        location: null,
        error: { code: 0, message: "Geolocation is not supported." },
      });
      return;
    }

    const requestSession = session;
    emit({ status: "requesting", location: null, error: null });

    const handleSuccess = (position) => {
      if (!enabled || requestSession !== session) return;
      const location = normalizeCoordinate(
        [position?.coords?.longitude, position?.coords?.latitude],
        { rejectZeroPair: true },
      );
      if (!location) return;

      emit({
        permission: "granted",
        status: "active",
        location,
        error: null,
      });
    };

    const handleError = (error) => {
      if (!enabled || requestSession !== session) return;
      const normalizedError = normalizeError(error);
      const denied = normalizedError.code === 1;
      enabled = false;
      invalidateSession();
      emit({
        permission: denied ? "denied" : state.permission,
        status: "error",
        location: null,
        error: normalizedError,
      });
    };

    let resolvedWatchId;
    try {
      resolvedWatchId = geolocation.watchPosition(
        handleSuccess,
        handleError,
        watchOptions,
      );
    } catch (error) {
      handleError(error);
      return;
    }

    if (!enabled || requestSession !== session) {
      geolocation.clearWatch?.(resolvedWatchId);
      return;
    }

    watchId = resolvedWatchId;
  };

  const setPermission = (permission) => {
    const normalizedPermission = ["prompt", "granted", "denied"].includes(
      permission,
    )
      ? permission
      : "unknown";

    if (normalizedPermission === "denied" && enabled) {
      enabled = false;
      invalidateSession();
      emit({
        permission: "denied",
        status: "error",
        location: null,
        error: { code: 1, message: "Location permission was denied." },
      });
      return;
    }

    emit({ permission: normalizedPermission });
  };

  return {
    getState: () => state,
    setPermission,
    start,
    stop,
    destroy: stop,
  };
}
